import React, { useCallback, /* useEffect, */ useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { fluid } from '../utils/responsive';
import DonutChart from 'shared/components/Graph/DonutChart';
import CountUpNumber from './CountUpNumber';
import VideoCourseIcon from '../../../pages/userManagementV2/dashboardV3/video-course-icon.svg';
import StudyMaterialsIcon from '../../../pages/userManagementV2/dashboardV3/study-materials-icon.svg';
import TestSeriesIcon from '../../../pages/userManagementV2/dashboardV3/test-series-icon.svg';
import LiveCoursesIcon from '../../../pages/userManagementV2/dashboardV3/live-courses-icon.svg';
import DateRangeFilterPopover from './DateRangeFilterPopover';
import { getStudentStats } from 'actions/studentManagement';

const DONUT_SIZE = 191;
const DONUT_GAP_DEGREES = 13;

const OverviewCard = React.memo(({ dotColor, iconBg, icon, value, description, countText, onOpenClick }) => (
    <div className="dv2-overview-card" style={{
        width: "100%",
        height: "120px",
        backgroundColor: "#F9F9F9",
        borderRadius: "25px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxSizing: "border-box",
    }}>
        {/* Top row — the icon is vertically centered against the whole
            [count + description] text block, not just the count line. */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "3px",
                        backgroundColor: dotColor,
                        flexShrink: 0,
                    }} />
                    {/* Counts up from 0, same as the total students count. */}
                    <CountUpNumber
                        value={value || 0}
                        delay={0.15}
                        style={{ fontSize: "22px", fontWeight: 500, color: "#191919", fontFamily: "Roboto, sans-serif", lineHeight: 1.1 }}
                    />
                </div>
                {/* One line (… if it doesn't fit, full text on hover) so the card's
                    fixed height always has room for the divider. */}
                <div title={description} style={{ fontSize: "14px", fontWeight: 400, color: "#636363", fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {description}
                </div>
            </div>
            <div className="dv2-overview-card-icon" style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                backgroundColor: iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}>
                <img src={icon} alt="" style={{ width: "24px", height: "24px" }} />
            </div>
        </div>

        {/* Divider line */}
        <div style={{ width: "100%", height: "1px", backgroundColor: "#E6E6E6", flexShrink: 0 }} />

        {/* Bottom row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "13px", fontWeight: 400, color: "#636363", fontFamily: "Roboto, sans-serif" }}>
                {countText}
            </span>
            <span onClick={onOpenClick} style={{ fontSize: "14px", fontWeight: 700, color: "#594099", fontFamily: "Roboto, sans-serif", cursor: "pointer" }}>
                OPEN
            </span>
        </div>
    </div>
));

OverviewCard.displayName = 'OverviewCard';

const unwrapStatsPayload = (res) => {
    if (!res) return {};
    let obj = res;
    for (let i = 0; i < 3; i++) {
        if (obj && typeof obj === 'object') {
            if (obj.total !== undefined || obj.byCourseType !== undefined || obj.unassignedStudents !== undefined) {
                return obj;
            }
            if (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data)) {
                obj = obj.data;
            } else {
                break;
            }
        }
    }
    return obj || {};
};

const StudentDistributionCard = React.memo(({ isMobile = false, getStudentStats, reduxStudentStats, onRangeLoading }) => {
    const history = useHistory();
    const location = useLocation();
    const basePath = location.pathname.startsWith('/user-management-v2') ? '/user-management-v2' : '/user-management';
    const [localStats, setLocalStats] = useState(null);
    // True once Last 30 days / a custom range is applied — then the card shows its own
    // range response; otherwise it shows the shared ?year= response from redux.
    const [rangeApplied, setRangeApplied] = useState(false);

    // Shared by the initial mount fetch and DateRangeFilterPopover's onApply
    // — params is { range: 'all' } / { range: 'last30' } / { range: 'custom',
    // startDate, endDate }, exactly what /userv2/student/stats expects.
    const fetchStats = useCallback((params) => {
        if (params) {
            onRangeLoading?.(true);
        }
        const safety = params ? setTimeout(() => onRangeLoading?.(false), 5000) : null;
        getStudentStats?.({ ...(params || {}), skipRedux: true }, (response) => {
            if (safety) clearTimeout(safety);
            onRangeLoading?.(false);
            const payload = unwrapStatsPayload(response);
            if (payload && (payload.total !== undefined || payload.byCourseType || payload.unassignedStudents !== undefined)) {
                setLocalStats(payload);
            }
        });
    }, [getStudentStats, onRangeLoading]);

    // No call of its own on load — counts come from ActiveStudentsCard's ?year= call (redux).
    // useEffect(() => {
    //     fetchStats({ range: 'all' });
    // }, [fetchStats]);

    const handleRangeApply = useCallback((params) => {
        if (!params || params.range === 'all') {
            // All time = the shared response; no extra call.
            setRangeApplied(false);
            setLocalStats(null);
            return;
        }
        setRangeApplied(true);
        fetchStats(params);
    }, [fetchStats]);

    // Redux is read directly below now, so later refreshes (status changes) show too.
    // useEffect(() => {
    //     if (!localStats) {
    //         const payload = unwrapStatsPayload(reduxStudentStats);
    //         if (payload && (payload.total !== undefined || payload.byCourseType || payload.unassignedStudents !== undefined)) {
    //             setLocalStats(payload);
    //         }
    //     }
    // }, [reduxStudentStats, localStats]);

    const stats = unwrapStatsPayload((rangeApplied && localStats) || reduxStudentStats || {});
    const byCourseType = stats.byCourseType || {};

    const courseData = byCourseType.course || byCourseType.liveCourse || byCourseType.liveCourses || byCourseType.live || {};
    const videoData = byCourseType.videoResource || byCourseType.videoCourse || byCourseType.videoResources || byCourseType.video_resource || {};
    const studyData = byCourseType.studyMaterial || byCourseType.studyMaterials || byCourseType.study_material || {};
    const testData = byCourseType.testSeries || byCourseType.onlineTestSeries || byCourseType.test_series || {};

    const getEnrolled = (data) => {
        if (data === undefined || data === null) return 0;
        if (typeof data === 'number') return data;
        if (typeof data === 'object') {
            return data.enrolled ?? data.totalEnrolled ?? data.studentCount ?? data.studentsCount ?? data.count ?? 0;
        }
        return Number(data) || 0;
    };

    const getCoursesTotal = (data) => {
        if (data === undefined || data === null) return 0;
        if (typeof data === 'object') {
            // `total` for every type; classroom overrides this with `catalogTotal`
            // at the call site (liveCoursesCount).
            return data.total ?? data.catalogTotal ?? data.courseCount ?? data.coursesCount ?? 0;
        }
        return 0;
    };

    const getBatchesTotal = (data) => {
        if (data === undefined || data === null) return 0;
        if (typeof data === 'object') {
            return data.batches ?? data.batchesCount ?? data.batchCount ?? 0;
        }
        return 0;
    };

    const liveEnrolled = getEnrolled(courseData);
    // Classroom courses show catalogTotal (includes catalog courses); falls back
    // to the usual total if the API doesn't send it.
    const liveCoursesCount = courseData?.catalogTotal ?? getCoursesTotal(courseData);
    const liveBatchesCount = getBatchesTotal(courseData);

    const videoEnrolled = getEnrolled(videoData);
    const videoCoursesCount = getCoursesTotal(videoData);

    const studyEnrolled = getEnrolled(studyData);
    const studyCoursesCount = getCoursesTotal(studyData);

    const testEnrolled = getEnrolled(testData);
    const testCoursesCount = getCoursesTotal(testData);

    const totalStudentsCount = stats.total ?? (liveEnrolled + videoEnrolled + studyEnrolled + testEnrolled);
    const unassignedCount = stats.unassignedStudents ?? 0;

    const donutSegments = useMemo(() => {
        const rawSegments = [
            { key: 'unassigned', value: Number(unassignedCount) || 0, color: '#C4C4C4' },
            { key: 'live', value: Number(liveEnrolled) || 0, color: '#1982C4' },
            { key: 'video', value: Number(videoEnrolled) || 0, color: '#6A994E' },
            { key: 'study', value: Number(studyEnrolled) || 0, color: '#E8505B' },
            { key: 'test', value: Number(testEnrolled) || 0, color: '#F2B63A' },
        ];
        const nonZero = rawSegments.filter((s) => s.value > 0);
        if (nonZero.length === 0) {
            return [{ key: 'empty', value: 1, color: '#E6E6E6' }];
        }
        return nonZero;
    }, [unassignedCount, liveEnrolled, videoEnrolled, studyEnrolled, testEnrolled]);

    return (
        <div className="dv2-staffdist-card" style={{
            backgroundColor: "#FFFFFF",
            borderRadius: fluid(25),
            padding: isMobile ? "18px" : `${fluid(24)} ${fluid(28)}`,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            fontFamily: "Roboto, sans-serif",
        }}>
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: fluid(18),
                flexShrink: 0,
                gap: fluid(12),
                flexWrap: "wrap",
            }}>
                <span style={{ fontSize: fluid(24), fontWeight: 700, color: "#191919" }}>
                    Students distribution
                </span>

                <DateRangeFilterPopover onApply={handleRangeApply} />
            </div>

            <div className="dv2-staffdist-body" style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "stretch",
                gap: isMobile ? "20px" : fluid(28),
                flex: 1,
                minHeight: 0,
            }}>
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    flexShrink: 0,
                    minWidth: 0,
                    width: isMobile ? "100%" : "220px",
                    height: isMobile ? "auto" : "100%",
                    paddingBottom: isMobile ? 0 : "8px",
                }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                        <CountUpNumber
                            value={totalStudentsCount || 0}
                            delay={0.15}
                            style={{ fontSize: fluid(32), fontWeight: 700, color: "#191919", lineHeight: 1.1 }}
                        />
                        <span style={{ fontSize: fluid(18), fontWeight: 400, color: "#636363" }}>
                            total students
                        </span>
                    </div>

                    <div style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                        <DonutChart
                            fluid
                            size={150}
                            strokeWidth={12}
                            gapDegrees={DONUT_GAP_DEGREES}
                            segments={donutSegments}
                        >
                            <span style={{ fontSize: "13px", fontWeight: 400, color: "#636363", fontFamily: "Roboto, sans-serif" }}>Types</span>
                            <span style={{ fontSize: "18px", fontWeight: 700, color: "#191919", marginTop: "4px", fontFamily: "Roboto, sans-serif" }}>4</span>
                        </DonutChart>
                    </div>

                    {/* Unassigned Students Link */}
                    <div
                        onClick={() => history.push(`${basePath}/students/unassigned`)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            marginTop: "16px",
                            justifyContent: "center",
                        }}
                    >
                        <span style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "4px",
                            backgroundColor: "#C4C4C4",
                            flexShrink: 0,
                        }} />
                        <span style={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#594099",
                        }}>
                            {unassignedCount} unassigned students
                        </span>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ display: "block", width: "20px", height: "20px", flexShrink: 0 }}>
                            <path d="M9 5L16 12L9 19" stroke="#594099" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>

                <div style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                    gap: fluid(22),
                    width: "100%",
                    minWidth: 0,
                    flex: 1,
                }}>
                    <OverviewCard
                        dotColor="#1982C4"
                        iconBg="#D8F0FF"
                        icon={VideoCourseIcon}
                        value={liveEnrolled}
                        description="Students in classroom courses"
                        countText={`${liveCoursesCount} courses | ${liveBatchesCount} batches`}
                        onOpenClick={() => history.push(`${basePath}/live-courses`)}
                    />
                    <OverviewCard
                        dotColor="#6A994E"
                        iconBg="#DCF2B7"
                        icon={StudyMaterialsIcon}
                        value={videoEnrolled}
                        description="Students in video courses"
                        countText={`${videoCoursesCount} courses`}
                        onOpenClick={() => history.push(`${basePath}/video-courses`)}
                    />
                    <OverviewCard
                        dotColor="#E8505B"
                        iconBg="#FFDEDF"
                        icon={TestSeriesIcon}
                        value={studyEnrolled}
                        description="Students in study materials"
                        countText={`${studyCoursesCount} courses`}
                        onOpenClick={() => history.push(`${basePath}/study-materials`)}
                    />
                    <OverviewCard
                        dotColor="#F2B63A"
                        iconBg="#FFEFC5"
                        icon={LiveCoursesIcon}
                        value={testEnrolled}
                        description="Students in test series"
                        countText={`${testCoursesCount} courses`}
                        onOpenClick={() => history.push(`${basePath}/test-series`)}
                    />
                </div>
            </div>
        </div>
    );
});

StudentDistributionCard.displayName = 'StudentDistributionCard';

const mapStateToProps = (state) => ({
    reduxStudentStats: state?.studentManagement?.studentStats || {},
});

const mapDispatchToProps = (dispatch) => ({
    getStudentStats: (params, callback) => dispatch(getStudentStats(params, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StudentDistributionCard);
